import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useWebAuthn } from "@/hooks/useWebAuthn";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Wallet, Mail, Lock, ArrowRight, User, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { z } from "zod";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import PasskeyPrompt from "@/components/PasskeyPrompt";
import LostPasskeyFlow from "@/components/LostPasskeyFlow";

const Auth: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(searchParams.get("mode") === "signup");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // OTP verification state
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCountdown, setOtpCountdown] = useState(0);
  // Store signup credentials for after OTP verification
  const [pendingSignup, setPendingSignup] = useState<{ email: string; password: string; fullName: string } | null>(null);
  // Passkey prompt after signup
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [showLostPasskey, setShowLostPasskey] = useState(false);

  const { signIn, signUp, user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { isAvailable: biometricAvailable, loading: biometricLoading, register: registerBiometric, login: loginBiometric } = useWebAuthn();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const getBiometricLabel = () => {
    return "مفتاح Passkey";
  };

  const getBiometricIcon = () => {
    return <KeyRound className="w-5 h-5" />;
  };

  useEffect(() => {
    if (searchParams.get("banned") === "true") {
      toast({ title: "تم حظر الحساب", description: "تم حظر حسابك من قبل الإدارة. تواصل مع الدعم الفني للمزيد.", variant: "destructive" });
    }
  }, []);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCountdown]);

  const signUpSchema = z.object({
    fullName: z.string().min(2, t('auth.validation.nameMin')).max(100),
    email: z.string().email(t('auth.validation.emailInvalid')).max(255),
    password: z.string().min(6, t('auth.validation.passwordMin')).max(100),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('auth.validation.passwordMismatch'),
    path: ["confirmPassword"],
  });

  const signInSchema = z.object({
    email: z.string().email(t('auth.validation.emailInvalid')).max(255),
    password: z.string().min(1, t('auth.validation.passwordRequired')).max(100),
  });

  useEffect(() => {
    if (user && !roleLoading) {
      if (isAdmin) {
        navigate("/admin");
      } else {
        navigate("/wallet");
      }
    }
  }, [user, isAdmin, roleLoading, navigate]);

  const handleBiometricLogin = async () => {
    const result = await loginBiometric();
    if (result.success) {
      // Delay setting biometric flag to ensure it's set AFTER the SIGNED_IN event clears it
      setTimeout(() => {
        localStorage.setItem("biometric_authenticated", "true");
        localStorage.setItem("mfa_verified", JSON.stringify({ userId: result.success ? user?.id : "", timestamp: Date.now() }));
        localStorage.removeItem("mfa_required");
      }, 500);
      toast({ title: "تم تسجيل الدخول بنجاح ✅" });
    } else {
      toast({
        title: "فشل تسجيل الدخول بـ Passkey",
        description: result.error || "حدث خطأ أثناء المصادقة",
        variant: "destructive",
      });
    }
  };

  const sendOtp = async (targetEmail: string) => {
    setOtpSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("signup-otp", {
        body: { action: "send", email: targetEmail },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setOtpCountdown(60);
      toast({ title: "تم إرسال رمز التحقق ✉️", description: "تحقق من بريدك الإلكتروني" });
    } catch (err: any) {
      toast({ title: t('auth.error'), description: err.message || "فشل إرسال الرمز", variant: "destructive" });
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtpAndSignUp = async () => {
    if (otpCode.length !== 6 || !pendingSignup) return;
    setOtpLoading(true);
    try {
      // Verify OTP first
      const { data, error } = await supabase.functions.invoke("signup-otp", {
        body: { action: "verify", email: pendingSignup.email, otp: otpCode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.verified) throw new Error("فشل التحقق");

      // OTP verified - now create the account
      const { error: signUpError } = await signUp(pendingSignup.email, pendingSignup.password, pendingSignup.fullName);
      if (signUpError) {
        if (signUpError.message.includes("already registered")) {
          toast({ title: t('auth.alreadyRegistered'), description: t('auth.alreadyRegisteredDesc'), variant: "destructive" });
        } else {
          throw signUpError;
        }
      } else {
        toast({ title: t('auth.accountCreated'), description: t('auth.welcomeToPlatform') });
        setShowOtpStep(false);
        setPendingSignup(null);
        // Show Passkey prompt for new user
        setShowPasskeyPrompt(true);
      }
    } catch (err: any) {
      toast({ title: t('auth.error'), description: err.message || "رمز غير صحيح أو منتهي الصلاحية", variant: "destructive" });
      setOtpCode("");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      if (isSignUp) {
        signUpSchema.parse({ fullName, email, password, confirmPassword });
      } else {
        signInSchema.parse({ email, password });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          newErrors[err.path[0]] = err.message;
        });
        setErrors(newErrors);
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignUp) {
        // Send OTP first, don't create account yet
        setPendingSignup({ email, password, fullName });
        setOtpEmail(email);
        await sendOtp(email);
        setShowOtpStep(true);
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast({ title: t('auth.invalidCredentials'), description: t('auth.invalidCredentialsDesc'), variant: "destructive" });
          } else if (error.message.includes("Email not confirmed")) {
            toast({ title: t('auth.emailNotConfirmed'), description: t('auth.emailNotConfirmedDesc'), variant: "destructive" });
          } else if (error.message.includes("banned") || error.message.includes("User is banned")) {
            toast({ title: "تم حظر الحساب", description: "تم حظر حسابك من قبل الإدارة. تواصل مع الدعم الفني للمزيد.", variant: "destructive" });
          } else {
            throw error;
          }
        }
      }
    } catch (error: any) {
      toast({ title: t('auth.error'), description: error?.message || t('auth.errorDesc'), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setErrors({});
    setFullName("");
    setPassword("");
    setConfirmPassword("");
    setShowOtpStep(false);
    setOtpCode("");
    setPendingSignup(null);
  };

  // Passkey prompt after signup
  if (showPasskeyPrompt) {
    return (
      <PasskeyPrompt
        open={true}
        onComplete={() => {
          setShowPasskeyPrompt(false);
          // User will be redirected by the useEffect that watches `user`
        }}
      />
    );
  }

  // OTP Verification Step UI
  if (showOtpStep) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 shadow-glow mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">تأكيد البريد الإلكتروني</h1>
            <p className="text-muted-foreground mt-2">
              أدخل رمز التحقق المكون من 6 أرقام المرسل إلى
            </p>
            <p className="text-primary font-medium mt-1 dir-ltr" dir="ltr">{otpEmail}</p>
          </div>

          <div className="glass rounded-2xl p-8 shadow-card animate-scale-in">
            <div className="flex flex-col items-center space-y-6">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={setOtpCode}
                disabled={otpLoading}
              >
                <InputOTPGroup className="gap-2" dir="ltr">
                  <InputOTPSlot index={0} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={1} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={2} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={3} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={4} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                  <InputOTPSlot index={5} className="w-12 h-14 text-xl font-bold border-border bg-input" />
                </InputOTPGroup>
              </InputOTP>

              <Button
                onClick={verifyOtpAndSignUp}
                disabled={otpCode.length !== 6 || otpLoading}
                className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg"
              >
                {otpLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    تأكيد وإنشاء الحساب
                    <ArrowRight className="w-5 h-5 mr-2" />
                  </>
                )}
              </Button>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">لم يصلك الرمز؟</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => sendOtp(otpEmail)}
                  disabled={otpSending || otpCountdown > 0}
                  className="text-primary"
                >
                  {otpCountdown > 0
                    ? `إعادة الإرسال بعد ${otpCountdown} ثانية`
                    : otpSending
                      ? "جاري الإرسال..."
                      : "إعادة إرسال الرمز"
                  }
                </Button>
              </div>

              <Button
                variant="link"
                onClick={() => {
                  setShowOtpStep(false);
                  setPendingSignup(null);
                  setOtpCode("");
                }}
                className="text-muted-foreground"
              >
                العودة للتسجيل
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary shadow-glow mb-4">
            <Wallet className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">{t('brand')}</h1>
          <p className="text-muted-foreground mt-2">
            {isSignUp ? t('auth.createAccount') : t('auth.welcomeBack')}
          </p>
          <p className="text-muted-foreground/70 text-sm mt-1">
            {t('auth.platformDesc')}
          </p>
        </div>

        <div className="glass rounded-2xl p-8 shadow-card animate-scale-in">
            <form onSubmit={handleSubmit} className="space-y-5">
                {isSignUp && (
                  <div className="space-y-2">
                    <Label className="text-foreground">{t('auth.fullName')}</Label>
                    <div className="relative">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input type="text" placeholder={t('auth.namePlaceholder')} value={fullName} onChange={(e) => setFullName(e.target.value)} className="bg-input border-border text-foreground pr-10" />
                    </div>
                    {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-foreground">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input type="email" placeholder="your@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-input border-border text-foreground pr-10 text-left" dir="ltr" />
                  </div>
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground">{t('auth.password')}</Label>
                    {!isSignUp && <ForgotPasswordModal />}
                  </div>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-input border-border text-foreground pr-10" />
                  </div>
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>

                {isSignUp && (
                  <div className="space-y-2">
                    <Label className="text-foreground">{t('auth.confirmPassword')}</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-input border-border text-foreground pr-10" />
                    </div>
                    {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                  </div>
                )}

                <Button type="submit" disabled={loading} className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg group">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {isSignUp ? t('auth.signUp') : t('auth.signIn')}
                      <ArrowRight className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
                    </>
                  )}
                </Button>
              </form>

              {/* Biometric Login Button */}
              {!isSignUp && biometricAvailable && (
                <div className="mt-4">
                  <div className="relative flex items-center justify-center my-3">
                    <div className="border-t border-border flex-1" />
                    <span className="px-3 text-xs text-muted-foreground">أو</span>
                    <div className="border-t border-border flex-1" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full h-12 font-semibold text-base gap-3 border-primary/30 hover:bg-primary/5"
                  >
                    {getBiometricIcon()}
                    {biometricLoading ? "جارٍ التحقق..." : `تسجيل الدخول بـ ${getBiometricLabel()}`}
                  </Button>
                </div>
              )}

            <div className="mt-6 text-center space-y-2">
              <button type="button" onClick={switchMode} className="text-primary hover:underline">
                {isSignUp ? t('auth.haveAccount') : t('auth.noAccount')}
              </button>
              {!isSignUp && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowLostPasskey(true)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1.5 mx-auto mt-2"
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    فقدت مفتاح Passkey؟
                  </button>
                </div>
              )}
            </div>

            <LostPasskeyFlow open={showLostPasskey} onOpenChange={setShowLostPasskey} />
        </div>
      </div>
    </div>
  );
};

export default Auth;